// ============================================================================
// @orgazone/i18n — LES TEXTES D'INTERFACE, pour toutes les surfaces.
//
// UTILITÉ : « Annuler », « 3 lignes retenues », « Aucun résultat ». Ce sont NOS textes : ils se
// TRADUISENT, ils ne se surchargent pas. À ne pas confondre avec le VOCABULAIRE des champs d'article
// (« Saison », « Millésime »), qui appartient au MAGASIN et vit dans `@orgazone/sync-contract` — deux
// familles, deux mécaniques, et les mélanger ferait renommer un bouton par un commerçant.
//
// ⚠️ LE TEXTE DE RÉFÉRENCE EST LA CLÉ (`t('Marge')`), et ce n'est pas une facilité :
//    · il n'y a AUCUNE clé à inventer pour des milliers de textes, donc la migration d'un écran est
//      mécanique et ne crée pas un second vocabulaire technique à maintenir en parallèle ;
//    · le code reste LISIBLE — `t('Marge')` dit ce que l'écran affiche, là où `t('col.margin')` oblige
//      à ouvrir un fichier pour relire une ligne ;
//    · réécrire le texte de référence INVALIDE sa traduction, ce qui est le comportement voulu : une
//      phrase changée doit être retraduite, jamais héritée de l'ancienne.
//    Le prix est connu : deux textes identiques dans des sens différents partageraient une traduction.
//    D'où le séparateur de CONTEXTE, à n'employer que là où le cas se pose.
//
// ⚠️ UNE TRADUCTION MANQUANTE REND LE TEXTE DE RÉFÉRENCE, ET SE COMPTE. Jamais la clé, jamais du vide —
//    mais jamais en silence non plus. C'est un défaut MESURÉ dans la maquette de la caisse (1 371 clés
//    `fr` contre 1 364 `en` et 1 359 `es`, et son `t()` retombe sur le français sans un mot) : une
//    traduction absente y est indiscernable d'une traduction faite. `missingTexts()` les nomme, et
//    c'est à l'audit de chaque surface d'en faire une porte.
//
// ⚠️ LES CATALOGUES S'ENREGISTRENT, ILS NE SE DÉCLARENT PAS ICI. Chaque paquet et chaque écran apporte
//    le sien (`registerCatalogue`), ce qui permet de ne charger que la langue ACTIVE — et, le jour où
//    une application découpe ses écrans, de ne charger que ceux qu'elle affiche. Le moteur ne connaît
//    aucun texte : il ne sait que les assembler.
//
// ⚠️ IL NE CONNAÎT AUCUNE LANGUE NON PLUS. `configureI18n` lui dit laquelle sert de référence et
//    lesquelles existent : une liste fermée ici obligerait à livrer ce paquet pour qu'une application
//    ajoute une langue.
// ============================================================================

/**
 * Une traduction : un texte, ou ses formes quand la phrase est COMPTÉE.
 * ⚠️ `one` et `other` sont EXIGÉES, les autres catégories sont facultatives : toutes les langues ont
 *    au moins ces deux formes, et celles qui en ont plus (polonais, russe, arabe) les ajoutent sans
 *    que les catalogues déjà écrits deviennent invalides. Une catégorie absente retombe sur `other`.
 */
export type Translation =
  | string
  | ({ one: string; other: string } & Partial<Record<'zero' | 'two' | 'few' | 'many', string>>);

/** Un catalogue : le texte de RÉFÉRENCE (la clé) vers sa traduction dans une langue. */
export type Catalogue = Readonly<Record<string, Translation>>;

/**
 * Les variables d'une phrase — et `{n}` doit être un NOMBRE.
 *
 * 🔴 LE MOTEUR NE PEUT PAS COMPTER UNE CHAÎNE. `pluralForm` retombe sur `other` dès que `n` n'est
 *    pas numérique, donc une phrase comptée à qui l'on passe un nombre DÉJÀ FORMATÉ
 *    (`nombre(models)`, `x.toLocaleString()`) affiche « Appliquer à 1 modèles ? » — au singulier
 *    comme au pluriel, pour toujours. **Onze sites du dépôt étaient dans ce cas**, sur des écrans
 *    déclarés finis ET traduits : ni le relevé des textes en dur, ni la complétude, ni la garde des
 *    pluriels ne pouvaient le voir — les trois regardent la CLÉ, jamais la valeur.
 *
 * ⚠️ C'EST LE TYPAGE QUI LE GARDE, pas un audit : une garde que le compilateur peut tenir ne doit
 *    pas vivre ailleurs. Le formatage du compte, lui, se fait au RENDU — `{n}` reçoit le nombre.
 */
export type Variables = Readonly<Record<string, string | number>> & { readonly n?: number };

/** Ce que l'application déclare au moteur : sa langue de référence, et celles qui existent. */
export interface I18nConfig {
  /** La langue dans laquelle les clés sont écrites — celle vers laquelle tout retombe. */
  referenceLang: string;
  /** Les langues que cette application sait rendre. Une autre est refusée et retombe sur la référence. */
  knownLangs: readonly string[];
}

/**
 * Le séparateur de CONTEXTE : `t('Article|column')` cherche « Article|column » au catalogue et affiche
 * « Article ». À n'employer QUE là où deux sens partagent le même texte de référence.
 */
const CONTEXT_SEPARATOR = '|';

/**
 * LA RÈGLE DE PLURIEL D'UNE LANGUE, demandée à la plateforme — jamais écrite ici.
 *
 * ⚠️ UN SEUIL CODÉ EN DUR EST FAUX DÈS LA DEUXIÈME LANGUE, et ça s'est vu : la première version
 *    basculait à `n < 2`, ce qui est la règle FRANÇAISE (« 0 intervalle »). En anglais et en espagnol
 *    zéro prend le pluriel (« 0 intervals »), donc l'interface annonçait « 0 interval » — une faute
 *    qu'aucun type ni aucune porte ne peut voir, puisque le texte s'affiche bel et bien.
 * ⚠️ On MÉMOÏSE par langue : `Intl.PluralRules` se construit une fois, `select` s'appelle à chaque
 *    rendu de texte compté.
 */
const pluralRules = new Map<string, Intl.PluralRules>();

function pluralCategory(forLang: string, n: number): Intl.LDMLPluralRule {
  let rules = pluralRules.get(forLang);
  if (!rules) {
    // ⚠️ Une étiquette de langue malformée fait LEVER le constructeur. Le moteur ne connaissant pas les
    //    langues (c'est l'application qui les déclare), une faute de frappe en configuration ferait
    //    tomber l'écran au premier texte compté. On retombe alors sur la règle la plus répandue.
    try {
      rules = new Intl.PluralRules(forLang);
    } catch {
      rules = new Intl.PluralRules('en');
    }
    pluralRules.set(forLang, rules);
  }
  return rules.select(n);
}

let config: I18nConfig = { referenceLang: 'fr', knownLangs: ['fr'] };
let lang = config.referenceLang;

/** Les catalogues assemblés, par langue — remplis au fur et à mesure des enregistrements. */
const catalogues = new Map<string, Record<string, Translation>>();

/** Ce qu'on a demandé et qu'aucun catalogue n'a su rendre, par langue. */
const missing = new Map<string, Set<string>>();

/** Les abonnés qui doivent oublier ce qu'ils ont calculé quand la langue change. */
const watchers: (() => void)[] = [];

/**
 * Déclare la langue de RÉFÉRENCE et celles qui existent. À appeler une fois, au démarrage.
 * ⚠️ REMET LA LANGUE EN VIGUEUR À LA RÉFÉRENCE : configurer après avoir choisi une langue serait un
 *    ordre qui n'a pas de sens, et la garder silencieusement aurait pu la rendre invalide au regard
 *    de la nouvelle liste.
 */
export function configureI18n(next: I18nConfig): void {
  config = { referenceLang: next.referenceLang, knownLangs: [...next.knownLangs] };
  lang = config.referenceLang;
  missing.clear();
  for (const forget of watchers) forget();
}

/** La langue dans laquelle les clés sont écrites — celle vers laquelle tout retombe. */
export function referenceLang(): string {
  return config.referenceLang;
}

/** La langue en vigueur, lue AU RENDU — jamais capturée dans une constante de module. */
export function currentLang(): string {
  return lang;
}

/**
 * Pose la langue en vigueur. Une langue que l'application ne connaît pas retombe sur la référence.
 * ⚠️ JAMAIS UNE ERREUR : une langue retirée de la liste, ou lue dans une base plus vieille que le
 *    code, ne doit pas faire tomber une page. Elle retombe, et l'écran reste lisible.
 */
export function setLang(next: string | null | undefined): void {
  lang = typeof next === 'string' && config.knownLangs.includes(next) ? next : config.referenceLang;
  missing.clear();
  for (const forget of watchers) forget();
}

/** S'abonner au changement de langue — pour les caches qui en dépendent. */
export function onLangChange(forget: () => void): void {
  watchers.push(forget);
}

/**
 * Apporte un catalogue — un paquet, un écran, un fragment quelconque.
 *
 * ⚠️ IL S'AJOUTE, IL NE REMPLACE PAS : c'est ce qui permet à chaque écran d'apporter SES textes, donc
 *    de ne charger que la langue active et, le jour où une application découpe ses écrans, que ceux
 *    qu'elle affiche. Deux fragments qui porteraient la même clé : le DERNIER gagne, et c'est dit —
 *    un fragment d'écran doit pouvoir préciser un texte que le socle donne plus vaguement.
 * ⚠️ Une langue inconnue de la configuration est REFUSÉE : l'enregistrer créerait un catalogue que
 *    `setLang` ne pourrait jamais atteindre, et personne ne verrait l'erreur.
 */
export function registerCatalogue(forLang: string, catalogue: Catalogue): void {
  if (!config.knownLangs.includes(forLang)) {
    throw new Error(`Langue « ${forLang} » inconnue de cette application — déclarez-la dans configureI18n.`);
  }
  const target = catalogues.get(forLang) ?? {};
  Object.assign(target, catalogue);
  catalogues.set(forLang, target);
  // Un texte qui manquait peut venir d'arriver : le relevé des manques n'a plus de raison d'être.
  missing.delete(forLang);
}

/** Ce qui a été demandé et qu'aucun catalogue n'a su rendre, dans la langue en vigueur. */
export function missingTexts(): string[] {
  return [...(missing.get(lang) ?? [])].sort();
}

/** Le texte de référence d'une clé — son contexte retiré. */
function reference(key: string): string {
  const i = key.indexOf(CONTEXT_SEPARATOR);
  return i < 0 ? key : key.slice(0, i);
}

/**
 * Remplace `{name}` par ce que l'appelant donne.
 * ⚠️ UNE VARIABLE OUBLIÉE RESTE VISIBLE (`{name}` à l'écran) : un texte visiblement troué se corrige,
 *    un trou muet ne se remarque jamais.
 */
function interpolate(text: string, vars: Variables | undefined, compte = false): string {
  if (!vars) return text;
  return text.replace(/\{([a-zA-Z][a-zA-Z0-9]*)\}/g, (raw, name: string) => {
    const value = vars[name];
    if (value === undefined) return raw;
    // 🔴 LE COMPTE SE FORMATE ICI, ET NULLE PART AILLEURS. Onze sites du dépôt passaient un nombre
    //    DÉJÀ formaté à `{n}` pour obtenir « 1 234 » — et perdaient le pluriel du même coup, le
    //    moteur ne sachant pas compter une chaîne. Le formatage appartient donc au RENDU : les
    //    appelants passent le NOMBRE, et le reçoivent groupé.
    // ⚠️ SEUL LE COMPTE D'UNE PHRASE COMPTÉE EST GROUPÉ — et les deux conditions comptent. `{n}` est
    //    le trou du COMPTE par convention (Forme DD) ; mais une phrase SANS pluriel peut y mettre un
    //    numéro de page, et « page 1 500 » serait faux. Une phrase qui porte `one`/`other` parle bien
    //    d'une quantité : là, le groupement est ce que l'appelant allait chercher en formatant
    //    lui-même — au prix du pluriel.
    if (compte && name === 'n' && typeof value === 'number') return formatNumber(value);
    return String(value);
  });
}

/**
 * La forme à employer : celle que la langue en vigueur désigne pour `{n}`.
 * ⚠️ Sans `n` NUMÉRIQUE il n'y a rien à compter : on rend `other`, qui est la forme neutre.
 */
function pluralForm(translation: Translation, forLang: string, vars: Variables | undefined): string {
  if (typeof translation === 'string') return translation;
  const n = vars?.n;
  if (typeof n !== 'number') return translation.other;
  return translation[pluralCategory(forLang, n)] ?? translation.other;
}

/**
 * LE TEXTE À AFFICHER, dans la langue en vigueur.
 *
 * `t('Cancel')` · `t('{n} row', { n })` · `t('Article|column')`
 * ⚠️ À appeler AU RENDU, jamais au chargement d'un module : un texte capturé dans une constante de
 *    module figerait la langue de référence avant que celle du compte ne soit posée.
 * ⚠️ Elle accepte une clé DÉCLARÉE (`key()`) : c'est la seule porte par laquelle une telle clé devient lisible.
 */
export function t(declared: Translatable, vars?: Variables): string {
  const key = declared as unknown as string;
  const found = catalogues.get(lang)?.[key];
  if (found !== undefined) return interpolate(pluralForm(found, lang, vars), vars, typeof found !== 'string');
  // ⚠️ DANS LA LANGUE DE RÉFÉRENCE, L'ABSENCE EST NORMALE : la clé EST le texte, et son catalogue ne
  //    porte que les entrées qui ont un PLURIEL — sans elles, « {n} ligne » afficherait « 3 ligne ».
  //    Dans toute autre langue, une absence est un MANQUE, et il se retient.
  if (lang !== config.referenceLang) {
    let forLang = missing.get(lang);
    if (!forLang) { forLang = new Set(); missing.set(lang, forLang); }
    forLang.add(key);
  }
  // ⚠️ LE REPLI PASSE PAR LE CATALOGUE DE RÉFÉRENCE, QUI PORTE LES PLURIELS. Sans cela une traduction
  //    manquante rendrait « Filtres — 3 actif » : un français FAUTIF, là où le repli devait au moins
  //    rendre un français juste. La forme se choisit alors avec la règle de la langue de référence,
  //    puisque c'est bien son texte qu'on affiche.
  const fallback = catalogues.get(config.referenceLang)?.[key];
  if (fallback !== undefined) return interpolate(pluralForm(fallback, config.referenceLang, vars), vars, typeof fallback !== 'string');
  return interpolate(reference(key), vars);
}

/**
 * DÉCLARE QU'UN TEXTE FRANÇAIS EST UNE CLÉ — sans le traduire ici.
 *
 * `{ path: '/', label: key('Tableau de bord') }` … puis `t(entry.label)` au rendu.
 *
 * ⚠️ POURQUOI CETTE FONCTION NE FAIT RIEN, ET POURQUOI ELLE EST INDISPENSABLE. Certains textes vivent
 *    dans des DONNÉES évaluées au chargement du module — la table de navigation, les colonnes d'un
 *    tableau, les onglets d'un écran. Y appeler `t()` figerait la langue de référence avant que celle
 *    du compte n'arrive : c'est précisément ce que l'audit refuse. Les écrire nus, en revanche, les
 *    rendrait indiscernables d'un texte oublié — ni l'outil de migration ni le relecteur ne sauraient
 *    qu'ils sont traduits ailleurs.
 * ⚠️ ELLE REND SON ARGUMENT, TEL QUEL, et c'est tout ce qu'elle doit faire : la traduction a lieu au
 *    RENDU, là où la langue est connue. Ce qu'elle apporte est une DÉCLARATION lisible par l'humain
 *    comme par l'outil — la donnée reste une donnée qu'on lit, et la complétude reste exigible.
 * 🔴 ET SON TYPE N'EST PAS `string`, c'est tout le reste de son rôle. Rendue NUE — enfant JSX, `title`, valeur
 *    d'une phrase traduite —, une clé déclarée s'affiche en français pour tous les comptes, et quatre gardes
 *    restent vertes (Forme DL). Une garde par NOMS ne suit ni une prop, ni un alias, ni une déstructuration : il en
 *    est passé une vingtaine, des infobulles du Marketing aux pastilles des réceptions. Le COMPILATEUR, lui, les
 *    refuse toutes : seul `t()` accepte une `DeclaredKey`.
 */
export function key(text: string): DeclaredKey {
  return text as unknown as DeclaredKey;
}

/** La marque d'une clé déclarée — jamais une valeur à l'exécution, seulement un type. */
declare const DECLARED: unique symbol;

/**
 * UNE CLÉ DÉCLARÉE PAR `key()` — à l'exécution la chaîne française elle-même, pour le compilateur un objet que
 * seuls `t()` et `keyText()` savent lire.
 */
export type DeclaredKey = { readonly [DECLARED]: true };

/**
 * CE QUE `t()` RÉSOUT — un texte ou une clé déclarée. Le type d'un champ de donnée qui porte l'un ou l'autre (un
 * libellé de table, un titre d'onglet) : rendu nu, il est refusé, puisqu'une `DeclaredKey` n'est pas affichable.
 */
export type Translatable = string | DeclaredKey;

/**
 * LE TEXTE D'UNE CLÉ QUAND ELLE SERT D'IDENTIFIANT — une clé React, une comparaison, une table indexée.
 * ⚠️ JAMAIS POUR L'AFFICHER : ce serait exactement le rendu nu que le type refuse, par la porte de service.
 *    `audit-cles-declarees` relève chaque appel rendu à l'écran.
 */
export function keyText(k: Translatable): string {
  return k as unknown as string;
}

/**
 * UN NOMBRE, ÉCRIT DANS LA LANGUE EN VIGUEUR — « 1 234 » en français, « 1,234 » en anglais.
 * ⚠️ POURQUOI CETTE FONCTION EXISTE : les écrans appelaient `toLocaleString('fr-FR')`, une langue
 *    GRAVÉE — un compte espagnol lisait ses nombres à la française, sans que rien ne le signale.
 * ⚠️ CE N'EST PAS LE RENDU DES MONTANTS : l'argent passe par `@orgazone/money-format`, qui tient
 *    l'échelle et la devise du magasin. Ici, un simple compte de lignes.
 */
export function formatNumber(value: number, opts?: { fractionDigits?: number }): string {
  // ⚠️ `fractionDigits` FIXE les décimales (« 1,80 », « 20,7 ») : un indicateur se lit en colonne, et une
  //    décimale qui apparaît et disparaît d'une carte à l'autre ferait sauter l'alignement.
  const digits = opts?.fractionDigits;
  const options = digits === undefined ? undefined : { minimumFractionDigits: digits, maximumFractionDigits: digits };
  // 🔴 ELLE LEVAIT SUR UNE ÉTIQUETTE MALFORMÉE, comme `Intl.PluralRules` juste au-dessus — et ce
  //    voisin-là était DÉJÀ gardé. Le défaut ne s'est vu qu'en la branchant au rendu du compte : une
  //    faute de frappe en configuration faisait alors tomber l'écran au premier texte compté, très
  //    loin de sa cause. **Une garde posée pour un appelant vaut pour tous les autres.**
  try {
    return value.toLocaleString(lang, options);
  } catch {
    return digits === undefined ? String(value) : value.toFixed(digits);
  }
}

/**
 * Remet le moteur à neuf — RÉSERVÉ AUX TESTS.
 * ⚠️ Sans cela, un cas de test hériterait des catalogues du précédent et passerait au vert pour une
 *    raison qui n'a rien à voir avec ce qu'il prétend éprouver.
 */
export function resetI18nForTests(): void {
  catalogues.clear();
  missing.clear();
  watchers.length = 0;
  config = { referenceLang: 'fr', knownLangs: ['fr'] };
  lang = config.referenceLang;
}
